import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  IbanInvoiceApi,
  IbanInvoicePaymentApi,
  IbanInvoicePaymentPaginatedResponseDto,
  IbanInvoicePaymentResponseDto,
  IbanInvoicePaymentUpdateStatusCommand,
  IbanInvoiceResponseDto,
} from "api";

import { IbanInvoice } from "./ibanInvoiceSlice";

const ibanInvoiceApi = new IbanInvoiceApi();
const ibanInvoicePaymentApi = new IbanInvoicePaymentApi();

export interface IbanInvoicePayment extends IbanInvoicePaymentResponseDto {
  id: string;
}

export interface IbanInvoiceDetailsState {
  invoice: IbanInvoice | null;

  payments: IbanInvoicePayment[] | null;
  pageCount: number;
  pageNumber: number;
  pageSize: number;
  totalCount: number;

  // analytics/metrics
}

const initialState: IbanInvoiceDetailsState = {
  invoice: null,
  payments: null,
  pageCount: 0,
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
};

export const getIbanInvoiceByUid = createAsyncThunk<
  IbanInvoiceResponseDto,
  string
>("ibanInvoiceDetails/getByUid", async (uid: string) => {
  const { data } = await ibanInvoiceApi.apiIbanInvoiceIbanInvoiceUidGet(uid);
  return data;
});

export const getIbanInvoicePaymentsByUid = createAsyncThunk<
  IbanInvoicePaymentPaginatedResponseDto,
  { uid: string; pageNumber?: number; pageSize?: number }
>(
  "ibanInvoiceDetails/getPaymentsByUid",
  async ({ uid, pageNumber = 1, pageSize = 10 }) => {
    const { data } =
      await ibanInvoiceApi.apiIbanInvoiceIbanInvoiceUidPaymentGet(
        uid,
        pageNumber,
        pageSize,
      );
    return data;
  },
);

export const updateIbanInvoicePaymentStatus = createAsyncThunk<
  IbanInvoicePaymentResponseDto,
  IbanInvoicePaymentUpdateStatusCommand
>("ibanInvoiceDetails/updatePaymentStatus", async (command) => {
  const { data } =
    await ibanInvoicePaymentApi.apiIbanInvoicePaymentStatusPut(command);
  return data;
});

const ibanInvoiceDetailsSlice = createSlice({
  name: "ibanInvoiceDetails",
  initialState,
  reducers: {
    setInvoice: (state, action) => {
      state.invoice = action.payload;
    },
    setPayments: (state, action) => {
      state.payments = action.payload;
    },
    setPageCount: (state, action) => {
      state.pageCount = action.payload;
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setPaymentStatus: (state, action) => {
      if (state.payments) {
        state.payments = state.payments.map((payment) =>
          payment.id === action.payload.id
            ? {
                ...payment,
                status: action.payload.status,
              }
            : payment,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getIbanInvoiceByUid.fulfilled, (state, action) => {
      state.invoice = {
        ...action.payload,
        id: action.payload.ibanQrInvoiceUid,
      };
    });
    builder.addCase(getIbanInvoicePaymentsByUid.fulfilled, (state, action) => {
      state.payments = action.payload.payments.map((payment) => ({
        ...payment,
        id: payment.ibanInvoicePaymentUid,
      }));
      state.pageCount = action.payload.pageCount;
      state.pageNumber = action.payload.pageNumber;
      state.pageSize = action.payload.pageSize;
      state.totalCount = action.payload.totalCount;
    });
  },
});

export const { setPageNumber, setInvoice, setPayments, setPaymentStatus } =
  ibanInvoiceDetailsSlice.actions;
export default ibanInvoiceDetailsSlice.reducer;
